import * as React from 'react'
import {
  AlertCircle,
  ImageOff,
  Loader2,
  MoreVertical,
  Pencil,
  Play,
  Star,
  Trash2,
  X,
} from 'lucide-react'

import { cn } from '@/shared/ui/cn'
import { env } from '@/shared/config/env'
import { type MosaicPhoto, resolvePhotoUrl } from '@/shared/domain'
import { formatDuration } from './format-duration'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/primitives'

type PhotoMosaicProps = {
  photos: MosaicPhoto[]
  onRemove?: (id: string) => Promise<void>
  onSetCover?: (id: string) => Promise<void>
  onEdit?: (id: string) => void
  onPhotoClick?: (id: string, index: number) => void
  canEdit: boolean
  className?: string
  leadingSlot?: React.ReactNode
}

function PhotoMosaic({
  photos,
  onRemove,
  onSetCover,
  onEdit,
  onPhotoClick,
  canEdit,
  className,
  leadingSlot,
}: PhotoMosaicProps) {
  if (photos.length === 0 && !leadingSlot) return null

  return (
    <div
      data-slot="photo-mosaic"
      className={cn('grid gap-2', className)}
      style={
        className
          ? undefined
          : { gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 200px))' }
      }
    >
      {leadingSlot}
      {photos.map((photo, index) => (
        <PhotoMosaicItem
          key={photo.id}
          photo={photo}
          index={index}
          canEdit={canEdit}
          onRemove={onRemove}
          onSetCover={onSetCover}
          onEdit={onEdit}
          onPhotoClick={onPhotoClick}
        />
      ))}
    </div>
  )
}

type PhotoMosaicItemProps = {
  photo: MosaicPhoto
  index: number
  canEdit: boolean
  onRemove?: (id: string) => Promise<void>
  onSetCover?: (id: string) => Promise<void>
  onEdit?: (id: string) => void
  onPhotoClick?: (id: string, index: number) => void
}

type PendingAction = 'remove' | 'setCover' | 'edit' | null

function PhotoMosaicItem({
  photo,
  index,
  canEdit,
  onRemove,
  onSetCover,
  onEdit,
  onPhotoClick,
}: PhotoMosaicItemProps) {
  const [isBusy, setIsBusy] = React.useState(false)
  const [pendingAction, setPendingAction] = React.useState<PendingAction>(null)
  const src = resolvePhotoUrl(photo, env.supabaseUrl)
  const isUploading = photo.uploadStatus === 'uploading'
  const isError = photo.uploadStatus === 'error'
  const isPending = isUploading || isError
  const canOfferEdit = Boolean(onEdit) && Boolean(photo.editable)
  const hasActions =
    !isPending &&
    canEdit &&
    !photo.readOnly &&
    (Boolean(onRemove) || Boolean(onSetCover) || canOfferEdit)
  const isInteractive = !isPending && Boolean(onPhotoClick)

  const handleClick = (e: React.MouseEvent) => {
    if (!isInteractive || !onPhotoClick) return
    const target = e.target as HTMLElement
    if (target.closest('[data-slot="photo-actions"]')) return
    onPhotoClick(photo.id, index)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isInteractive || !onPhotoClick) return
    const target = e.target as HTMLElement
    if (target.closest('[data-slot="photo-actions"]')) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onPhotoClick(photo.id, index)
    }
  }

  const stopPropagation = (event: React.MouseEvent | React.PointerEvent) => {
    event.stopPropagation()
  }

  const handleMenuClose = (e: Event) => {
    if (!pendingAction) return
    e.preventDefault()
    const action = pendingAction
    setPendingAction(null)
    if (action === 'edit') {
      onEdit?.(photo.id)
      return
    }
    const handler = action === 'remove' ? onRemove : onSetCover
    if (!handler) return
    setIsBusy(true)
    void handler(photo.id).finally(() => setIsBusy(false))
  }

  const handlePendingDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onRemove) return
    void onRemove(photo.id)
  }

  return (
    <div
      title={isError ? photo.errorMessage : undefined}
      className={cn(
        'group relative aspect-square overflow-hidden rounded-lg border border-border shadow-sm transition-shadow duration-300',
        !isPending && 'hover:shadow-md',
        isError && 'border-2 border-destructive',
        isBusy && 'opacity-50',
        isInteractive && 'cursor-pointer',
      )}
      {...(isInteractive
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: handleClick,
            onKeyDown: handleKeyDown,
          }
        : {})}
    >
      {src ? (
        <img
          src={src}
          alt={photo.caption ?? ''}
          className={cn(
            'size-full object-cover transition-transform duration-300',
            !isPending && 'group-hover:scale-105',
            isUploading && 'opacity-40 saturate-0',
            isError && 'opacity-50',
          )}
        />
      ) : (
        <div
          aria-label="Photo unavailable"
          className="flex size-full items-center justify-center bg-muted text-muted-foreground"
        >
          <ImageOff className="size-6" aria-hidden />
        </div>
      )}
      {!isPending ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        />
      ) : null}

      {isUploading ? (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-foreground">
          <Loader2 className="size-6 animate-spin" />
          <span className="text-xs font-medium">Uploading...</span>
        </div>
      ) : null}

      {isError ? (
        <>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-destructive">
            <AlertCircle className="size-6" />
            <span className="text-xs font-medium">Upload failed</span>
          </div>
          {onRemove ? (
            <Button
              type="button"
              variant="destructive"
              size="icon-xs"
              className="absolute right-1.5 top-1.5"
              onClick={handlePendingDismiss}
              aria-label="Dismiss failed upload"
            >
              <X />
            </Button>
          ) : null}
        </>
      ) : null}

      {!isPending && photo.mediaType === 'video' ? (
        <div
          aria-label="Play video"
          data-slot="video-play-badge"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="flex items-center justify-center rounded-full bg-black/40 p-3">
            <Play className="size-6 fill-current text-white" />
          </div>
        </div>
      ) : null}

      {!isPending && photo.mediaType === 'video' && typeof photo.durationSeconds === 'number' && photo.durationSeconds > 0 ? (
        <Badge
          variant="secondary"
          className="absolute bottom-1.5 right-1.5 text-2xs"
        >
          {formatDuration(photo.durationSeconds)}
        </Badge>
      ) : null}

      {photo.isCoverPhoto ? (
        <Badge
          variant="secondary"
          className="absolute left-1.5 top-1.5 text-2xs"
        >
          COVER
        </Badge>
      ) : null}

      {hasActions ? (
        <div data-slot="photo-actions">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="icon-xs"
                className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100"
                aria-label="Photo actions"
                onClick={stopPropagation}
                onPointerDown={stopPropagation}
              >
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={stopPropagation}
              onPointerDown={stopPropagation}
              onCloseAutoFocus={handleMenuClose}
            >
              {onSetCover && !photo.isCoverPhoto ? (
                <DropdownMenuItem
                  onSelect={() => setPendingAction('setCover')}
                  disabled={isBusy}
                >
                  <Star className="size-4" />
                  Set as cover
                </DropdownMenuItem>
              ) : null}
              {canOfferEdit ? (
                <DropdownMenuItem
                  onSelect={() => setPendingAction('edit')}
                  disabled={isBusy}
                >
                  <Pencil className="size-4" />
                  Edit
                </DropdownMenuItem>
              ) : null}
              {onRemove ? (
                <DropdownMenuItem
                  onSelect={() => setPendingAction('remove')}
                  disabled={isBusy}
                >
                  <Trash2 className="size-4" />
                  Remove
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </div>
  )
}

export { PhotoMosaic }
export type { PhotoMosaicProps, MosaicPhoto }
